/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import { indexBy, mergeRight, prop } from 'ramda';

import * as types from 'constants/ActionTypes';
import { FILTERS } from 'constants/ConfiguratorConstants';

const emptyFilters = { [FILTERS.FUEL]: '', [FILTERS.TRANSMISSION]: '', [FILTERS.BODY]: '', [FILTERS.TEXT]: '' };

/**
 * @typedef {{
 *   apiError: string,
 *   isFetching: boolean,
 *   currentUploadedModel: unknown,
 *   nbFilesToUpload: number,
 *   lengthToUpload: number,
 *   isUploading: boolean,
 *   data: {
 *     all: {
 *       page: import("modules/global/generic.types").Page,
 *       items: Record<string, import("modules/offers/types").DetailedModel>,
 *     },
 *     select: {
 *       page: import("modules/global/generic.types").Page,
 *       items: Record<string, import("modules/offers/types").Model>,
 *       links: Record<string, import("modules/global/generic.types").Link>,
 *     }
 *   },
 *   table: {
 *     pageSize: number,
 *     sort: string,
 *     searchString: string,
 *     onlyValidModels: boolean,
 *   },
 *   modal: {
 *      update: {
 *        isLoading: boolean,
 *        isVisible: boolean,
 *        error: string,
 *        modelId: string,
 *      }
 *   }
 * }} ModelReducer
 */

/** @type {ModelReducer} */
const initialState = {
  data: {
    all: { items: {}, page: {} },
    select: { items: {}, page: {}, links: {} },
  },
  currentSearch: { text: '' },
  filters: emptyFilters,
  apiError: null,
  isFetching: false,
  isUploading: false,
  nbFilesToUpload: 0,
  lengthToUpload: 0,
  currentUploadedModel: null,
  table: {
    pageSize: 10,
    sort: 'modifiedOn,desc',
    searchString: '',
    onlyValidModels: true,
  },
  modal: {
    update: {
      isLoading: false,
      isVisible: false,
      error: null,
      modelId: null,
    },
  },
};

const reducer = produce(
  /** @param {ModelReducer} draft  */
  (draft, action) => {
    switch (action.type) {
      case types.FETCH_MODEL_REQUEST:
      case types.FETCH_MODELS_REQUEST:
      case types.FETCH_MODELS_SELECT_REQUEST:
      case types.FETCH_MODELS_SELECT_NEXT_PAGE_REQUEST:
        draft.isFetching = true;
        draft.apiError = null;
        draft.table.pageSize = action.params?.size ?? 10;
        draft.table.sort = action.params?.sort ?? 'modifiedOn,desc';
        draft.table.searchString = action.params?.text ?? '';
        break;

      case types.FETCH_MODEL_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items[action.model.id] = action.model;
        draft.data.select.items[action.model.id] = action.model;
        break;

      case types.FETCH_MODELS_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items = indexBy(prop('id'))(action.models);
        draft.data.all.page = action.page;
        break;

      case types.FETCH_MODELS_SELECT_SUCCESS:
        draft.isFetching = false;
        draft.data.select.items = mergeRight(draft.data.select.items, indexBy(prop('id'))(action.models.content));
        draft.data.select.links = indexBy(prop('rel'))(action.models.links);
        draft.data.select.page = action.models.page;
        break;

      case types.FETCH_MODEL_FAILURE:
      case types.FETCH_MODELS_FAILURE:
      case types.FETCH_MODELS_SELECT_FAILURE:
        draft.isFetching = false;
        draft.apiError = action.error;
        break;

      case types.CLEAN_MODELS_SELECT:
        draft.data.select = {
          items: {},
          links: {},
          page: {},
        };
        break;

      case types.UPDATE_MODEL_SUCCESS:
        draft.modal.update.isLoading = false;
        draft.modal.update.isVisible = false;
        draft.data.all.items[action.payload.id] = action.payload;
        break;
      case types.UPDATE_MODEL_REQUEST:
        draft.modal.update.isLoading = true;
        break;
      case types.UPDATE_MODEL_FAILURE:
        draft.modal.update.isLoading = false;
        break;

      case types.SET_MODEL_FILTER:
        draft.filters = action.payload;
        break;

      case types.UPLOAD_MODEL_REQUEST:
        draft.currentUploadedModel = action.payload;
        break;

      case types.UPLOAD_MODELS_REQUEST:
        draft.isUploading = true;
        draft.lengthToUpload = action.payload.length;
        draft.nbFilesToUpload++;
        break;

      case types.UPLOAD_MODELS_SUCCESS:
      case types.UPLOAD_MODELS_FAILURE:
        draft.nbFilesToUpload--;
        if (draft.nbFilesToUpload === 0) {
          draft.isUploading = false;
          draft.currentUploadedModel = null;
          draft.lengthToUpload = 0;
        }
        break;
      case types.TOGGLE_VALID_MODELS:
        draft.table.onlyValidModels = !draft.table.onlyValidModels;
        break;

      case types.OPEN_UPDATE_MODAL:
        draft.modal.update.isVisible = true;
        draft.modal.update.error = null;
        draft.modal.update.modelId = action.payload.modelId;
        break;
      case types.CLOSE_UPDATE_MODAL:
        draft.modal.update.isVisible = false;
        draft.modal.update.modelId = null;
        break;

      case types.CLEAN_MODEL_FILTER:
        draft.filters = emptyFilters;
        break;

      case types.CLEAN_MODELS_DASHBOARD:
        draft.table = {
          pageSize: 10,
          sort: 'modifiedOn,desc',
          searchString: '',
          onlyValidModels: true,
        };
        draft.data.all = { items: {}, page: {} };
        break;

      default:
    }
  },
  initialState
);
export default reducer;
